import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney } from '../ui/format'
import { Banner, Button, Empty, ErrorBanner, Panel, Spinner } from '@shop/ui'

type Row = {
  userId: string
  fullNameAr: string
  days: number
  wages: number
  overtimeHours: number
  overtimePay: number
  commission: number
  earned: number
  opening: number
  paid: number
  balance: number
}

type Payroll = {
  from: string
  to: string
  rule: { dailyWage: number; normalHours: number; overtimeRate: number; step: number; perStep: number; startsOn: string }
  rows: Row[]
}

function monthStart(offset = 0): string {
  const now = new Date()

  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1)).toISOString().slice(0, 10)
}

const monthName = new Intl.DateTimeFormat('ar-JO-u-nu-latn', { month: 'long', year: 'numeric', timeZone: 'UTC' })

/**
 * رواتبُ الموظّفين — <b>طلبُ المالك ٢٠٢٦-١٠-٢٤</b>: «كم راتب كل موظف وكم يوم دوام … حطّه
 * صفحة بلوحة الإدارة».
 *
 * <p>
 * لكلّ موظّفٍ في الشهر: أيّامُه، ويوميّتُه، وساعاتُه الزيادة، وعمولتُه — ثمّ ما كان له
 * قبل الشهر، وما قبضه، والباقي. <b>بلا مدينٍ ولا دائن</b>.
 * </p>
 */
export default function Payroll() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [month, setMonth] = useState(monthStart())

  const data = useQuery({
    queryKey: ['payroll', month],
    queryFn: () => api<Payroll>(`/api/v1/payroll?month=${month}`),
    enabled: can(Perm.commissionView),
  })

  if (!can(Perm.commissionView)) {
    return (
      <Panel title="رواتب الموظّفين">
        <Banner tone="warn">هذه الصفحةُ تحتاج صلاحيّةَ «عرضُ العمولات».</Banner>
      </Panel>
    )
  }

  const view = data.data
  const rows = view?.rows ?? []
  const sum = (pick: (r: Row) => number) => rows.reduce((total, r) => total + pick(r), 0)

  return (
    <div className="grid gap-4">
      <Panel title="رواتب الموظّفين">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setMonth(monthStart(-1))}>الشهر الماضي</Button>
          <Button onClick={() => setMonth(monthStart())}>هذا الشهر</Button>
          <b className="ms-2">{monthName.format(new Date(`${month}T00:00:00Z`))}</b>
          <Button onClick={() => window.print()}>اطبع</Button>
        </div>

        {view && (
          <p className="mt-2 text-sm text-ink-soft">
            اليوميّة {formatMoney(view.rule.dailyWage)} عن {view.rule.normalHours} ساعات · كلّ ساعةٍ زيادة{' '}
            {formatMoney(view.rule.overtimeRate)} · العمولة {formatMoney(view.rule.perStep)} عن كلّ{' '}
            {formatMoney(view.rule.step)} من مبيعات المحلّ، تُقسم على المداومين.
          </p>
        )}

        <ErrorBanner error={data.error} />
      </Panel>

      {data.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Panel title="">
          <Empty>لا دوامَ ولا أرصدةَ في هذا الشهر.</Empty>
        </Panel>
      ) : (
        <>
          <Panel title="ما استحقّه كلُّ موظّف">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr>
                    <th className="p-2 text-start">الموظّف</th>
                    <th className="p-2 text-center">أيّام الدوام</th>
                    <th className="p-2 text-end">اليوميّات</th>
                    <th className="p-2 text-end">ساعات زيادة</th>
                    <th className="p-2 text-end">العمولة</th>
                    <th className="p-2 text-end">المستحقّ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.userId} className="border-t border-line">
                      <td className="p-2 font-semibold">{r.fullNameAr}</td>
                      <td className="tabular p-2 text-center">{r.days}</td>
                      <td className="tabular p-2 text-end" dir="ltr">{formatMoney(r.wages)}</td>
                      <td className="tabular p-2 text-end" dir="ltr">
                        {r.overtimeHours > 0 ? `${r.overtimeHours} h · ${formatMoney(r.overtimePay)}` : '—'}
                      </td>
                      <td className="tabular p-2 text-end" dir="ltr">{formatMoney(r.commission)}</td>
                      <td className="tabular p-2 text-end font-bold" dir="ltr">{formatMoney(r.earned)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-line font-bold">
                    <td className="p-2">المجموع</td>
                    <td className="tabular p-2 text-center">{sum((r) => r.days)}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.wages))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.overtimePay))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.commission))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.earned))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="قبض وباقي">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[36rem] text-sm">
                <thead className="text-xs text-ink-soft">
                  <tr>
                    <th className="p-2 text-start">الموظّف</th>
                    <th className="p-2 text-end">كان له قبل الشهر</th>
                    <th className="p-2 text-end">استحقّ</th>
                    <th className="p-2 text-end">قبض</th>
                    <th className="p-2 text-end">الباقي له</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.userId} className="border-t border-line">
                      <td className="p-2 font-semibold">{r.fullNameAr}</td>
                      <td className="tabular p-2 text-end" dir="ltr">{r.opening ? formatMoney(r.opening) : '—'}</td>
                      <td className="tabular p-2 text-end text-ok" dir="ltr">{formatMoney(r.earned)}</td>
                      <td className="tabular p-2 text-end text-warn" dir="ltr">{formatMoney(r.paid)}</td>
                      <td className="p-2 text-end font-bold">
                        <span className="tabular" dir="ltr">{formatMoney(Math.abs(r.balance))}</span>
                        {r.balance < 0 && <span className="ms-1 text-xs text-warn">سلفة</span>}
                      </td>
                      <td className="p-2 text-end print:hidden">
                        <button
                          type="button"
                          className="text-xs text-brand underline"
                          onClick={() => navigate(`/statement?tab=employees&id=${r.userId}`)}
                        >
                          كشف
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-line font-bold">
                    <td className="p-2">المجموع</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.opening))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.earned))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.paid))}</td>
                    <td className="tabular p-2 text-end" dir="ltr">{formatMoney(sum((r) => r.balance))}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-2 text-xs text-ink-soft">
              الأيّامُ والساعاتُ من «الدوام» — وعدّلها هناك إن نسي أحدٌ «وصلت» أو «روّحت». واليومُ الجاري يُحسب
              حيًّا ويُرحَّل صباحَ الغد.
            </p>
          </Panel>
        </>
      )}
    </div>
  )
}
